/* ServicesCrudCard.tsx
	Une card affichant la gestion CRUD des services
	Utilise le composant GenericCrudCard pour lister, ajouter, éditer et supprimer les éléments
	Utilise listPaginated pour bénéficier de la pagination, du tri et des filtres côté serveur
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faHandshake } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import { ServiceData, servicesApi } from 'features/services';

// Interface des props du composant
interface ServicesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
}

// ServicesCrudCard component
export const ServicesCrudCard: React.FC<ServicesCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/settings',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: ServiceData) => a.id }]
			: []),
		// Colonne "Label"
		{
			label: 'Label',
			field: 'label',
			render: (a: ServiceData) => a.label,
		},
		// Colonne "Description"
		{
			label: 'Description',
			field: 'description',
			render: (a: ServiceData) => a.description ?? '—',
		},
		// Colonne "Prix défaut"
		{
			label: 'Prix défaut',
			field: 'defaultPrice',
			render: (a: ServiceData) =>
				a.defaultPrice !== undefined ? `${a.defaultPrice.toFixed(2)} €` : '—',
		},
		// Colonne "Taux de taxe par défaut" (tri désactivé : relation liée)
		{
			label: 'Taux de taxe',
			field: 'none',
			render: (a: ServiceData) => a.defaultTaxRate?.label ?? '—',
		},
		// Colonne "A/V" : indique si le service est pour l'achat et/ou la vente
		{
			label: 'A/V',
			field: 'none',
			render: (a: ServiceData) =>
				(a.forPurchase ? 'A' : '') + (a.forSale ? 'V' : '') || '—',
		},
	];

	return (
		<GenericCrudCard<ServiceData>
			queryKey="services"
			title={size === 'sm' ? 'Services' : 'Liste des services'}
			fetchData={(token, options) => servicesApi.listPaginated(token, options)}
			deleteItem={servicesApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter un service"
			getAddPath={() => '/app/settings/services/new'}
			getEditPath={(id) => `/app/settings/services/${id}/edit`}
			emptyMessage="Aucun service trouvé."
			getReturnPath={() => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faHandshake : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default ServicesCrudCard;
